/**
 * Collection filter component with images.
 *
 * Displays collection options as image tiles with names overlaid.
 * Uses brand-specific collection images.
 * Falls back to text-only display if no image is available.
 * Preserves original ordering of options when selections are made.
 */

'use client';

import { CheckIcon } from '@/components/ui/CheckIcon';
import { Icon } from '@/components/ui/Icon';
import { getCollectionImages } from '@/lib/algolia/colorMappings';
import { extractFacetId, extractFacetLabel, facetValueMatchesId } from '@/lib/algolia/facetValues';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { BRAND } from '@/lib/cms/config';
import Image from 'next/image';
import { useMemo, useRef, useState } from 'react';
import styles from './CollectionFilter.module.scss';

interface CollectionFilterProps {
  /** Display name for the filter header */
  displayName: string;
  /** Facet values with counts: [fullValue, count][] */
  values: [string, number][];
  /** Currently active filter IDs from URL params */
  activeIds: string[];
  /** Optional: Collection data from CMS with images */
  collectionData?: Array<{
    name: string;
    image?: string;
  }>;
}

export function CollectionFilter({
  displayName,
  values,
  activeIds,
  collectionData,
}: CollectionFilterProps) {
  const [isExpanded, setIsExpanded] = useState(activeIds.length > 0);
  const { toggleFilterValue } = useSearchParams();

  // Track original order of facet values to prevent reordering on selection
  const originalOrderRef = useRef<string[] | null>(null);

  // Get brand-specific collection images
  const staticCollectionImages = getCollectionImages(BRAND);

  // Parse values and add image data
  const parsedValues = useMemo(() => {
    const parsed = values.map(([fullValue, count]) => {
      const id = extractFacetId(fullValue, 'collection');
      const label = extractFacetLabel(fullValue, 'collection');
      const isActive = activeIds.some((activeId) =>
        facetValueMatchesId(fullValue, activeId, 'collection')
      );

      // Try to get image from CMS data first, then static mapping
      const cmsCollection = collectionData?.find((c) => c.name === label);
      const image = cmsCollection?.image || staticCollectionImages[label];

      return {
        fullValue,
        id,
        label,
        count,
        isActive,
        image,
      };
    });

    // Capture original order on first render (when no active filters)
    if (originalOrderRef.current === null && parsed.length > 0) {
      originalOrderRef.current = parsed.map((p) => p.id);
    }

    // Sort by original order to prevent reordering when selections change
    if (originalOrderRef.current) {
      const orderMap = new Map(originalOrderRef.current.map((id, index) => [id, index]));
      parsed.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? Infinity;
        const orderB = orderMap.get(b.id) ?? Infinity;
        return orderA - orderB;
      });
    }

    return parsed;
  }, [values, activeIds, collectionData, staticCollectionImages]);

  const handleToggle = (id: string) => {
    toggleFilterValue('collection', id);
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(id);
    }
  };

  // Check if we have any images
  const hasImages = parsedValues.some((v) => v.image);

  return (
    <div className={`${styles.facet} ${isExpanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.name}>
          {displayName}
          {activeIds.length > 0 && <span className={styles.activeCount}>{activeIds.length}</span>}
        </span>
        <Icon
          name="chevron-down"
          size={20}
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
        />
      </button>

      {isExpanded && (
        <div className={`${styles.collectionGrid} ${hasImages ? styles.withImages : ''}`}>
          {parsedValues.map(({ id, label, count, isActive, image }) => (
            <div
              key={id}
              className={`${styles.collectionOption} ${isActive ? styles.active : ''} ${image ? styles.hasImage : ''}`}
              onClick={() => handleToggle(id)}
              onKeyDown={(e) => handleKeyDown(e, id)}
              tabIndex={0}
              role="checkbox"
              aria-checked={isActive}
              aria-label={`${label} (${count} items)`}
            >
              {/* Image background */}
              {image && (
                <div className={styles.imageWrapper}>
                  <Image
                    src={image}
                    alt={label}
                    fill
                    sizes="(max-width: 768px) 50vw, 33vw"
                    className={styles.image}
                  />
                  <div className={styles.imageOverlay} />
                </div>
              )}

              {/* Content */}
              <div className={styles.content}>
                <span className={styles.label}>{label}</span>
                <span className={styles.count}>({count})</span>
              </div>

              {/* Active indicator */}
              {isActive && (
                <div className={styles.activeIndicator}>
                  <CheckIcon size={14} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
